/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

import { useEffect, useState } from 'react';

type SetValue<T> = T | ((val: T) => T);

function useLocalStorage<T>(key: string, initialValue: T): [T, (value: SetValue<T>) => void] {
  // State to store our value
  // Pass  initial state function to useState so logic is only executed once
  const isBrowser =
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    window.localStorage !== null;

  const [storedValue, setStoredValue] = useState(() => {
    if (!isBrowser) {
      return initialValue;
    }

    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // useEffect to update local storage when the state changes
  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        typeof storedValue === 'function' ? storedValue(storedValue) : storedValue;
      // Save state
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  }, [isBrowser, key, storedValue]);

  return [storedValue, setStoredValue];
}

export default useLocalStorage;
